// Menu generation — candidate WEIGHTING factors.
//
// When the generator fills a meal slot or tops a day up, several viable
// candidates usually compete. Rather than a hard sort with a uniform random
// tie-break, we score each viable candidate on a set of independent FACTORS —
// each a value in [0,1] — combine them into a single weight (a weighted sum with
// hard-coded top-level FACTOR_WEIGHTS), and pick one at random by a TWO-STAGE
// grouped draw: first a source group (on-hand / make / buy) in proportion to its
// best weight, then a candidate within it by weight — so a lone leftover isn't
// drowned out by many cook/buy options (see groupedProbabilities). Expiry is
// scored relative to the PLANNED day (planningInstant). There are three factors:
//
//   • source — HOW the serving would be obtained, as a computed category score
//     (NOT a flat lookup): on-hand (1.0) > make (0.3) > buy (interpolated up from
//     0.1 toward make by the fraction of ingredients already on hand). See
//     SOURCE_THRESHOLDS + sourceScore; the category + coverage come from
//     classifySource in menu.js.
//   • expiry — an ABSOLUTE per-candidate score (NOT normalised across the slot's
//     candidate set): each dated draw the serving would take is scored on a log
//     curve (≤1 day → 1, ≥7 days → 0), averaged, then given a mild count bonus so
//     a meal clearing several expiring items ranks higher. NO perishable draws
//     → 0 (not a neutral default). See drawExpiryScore + expiryScore.
//   • rating — the user's effective [0,1] preference for the recipe/ingredient.
//
// This makes the selection policy PLUGGABLE: adding a new consideration later
// (e.g. "how healthy is this?", variety, day-of-week) is just another factor +
// weight, with no change to the placement machinery.
//
// Hard rules are NOT expressed as factors — they gate VIABILITY upstream (a
// candidate must be sourceable within tolerance; a recipe's own leftovers are
// always consumed before a fresh batch; a rating of 0 means "never"). Factors
// only bias the choice AMONG viable candidates.
//
// ── Debug-page coupling (IMPORTANT) ─────────────────────────────────────────
// The /debug/weights page (pages/debug-weights.js) is a FAITHFUL MIRROR of real
// generation: it does NOT re-implement scoring. It calls explainSlotCandidates /
// simulateSlotPicks in menu.js, which reuse the SAME internals the generator
// uses — bestSourceFor + classifySource for the per-candidate { source,
// availableFraction, draws }, then candidateWeights / pickWeightedCandidate here
// for the weights. So any change to these factor functions (or to how
// bestSourceFor/classifySource shape a candidate) automatically flows to BOTH
// real generation AND the debug visualiser. If you add/rename a factor, a source
// category, or a candidate field, update it HERE (and in bestSourceFor) and the
// debug page follows for free — do NOT fork the maths into the page. See the
// matching note on explainSlotCandidates in menu.js.
//
// Pure module (no DB / AWS); the weighted pick takes an injectable rng.

/**
 * Top-level factor weights. Hard-coded for now but intentionally isolated here
 * so they can later be surfaced (per-user settings or per-strategy) without
 * touching the generator. They need not sum to 1 — the pick normalises.
 */
export const FACTOR_WEIGHTS = Object.freeze({
  source: 0.5, // prefer using what you already have (on-hand > make > buy)
  expiry: 0.3, // prefer using soonest-expiring stock to reduce waste
  rating: 0.2, // prefer things the user likes more
});

/**
 * Source-factor THRESHOLDS (each a [0,1] anchor, injectable/tunable):
 *   • onHand — fully available, no prep needed (leftovers, or a no-prep recipe
 *              whose ingredients are all on hand). The best score.
 *   • make   — fully available but must cook something that requires prep.
 *   • buy    — the LOW end for a recipe that needs any buying; the actual buy
 *              score interpolates from `buy` (nothing on hand) UP toward `make`
 *              in proportion to how many of its ingredients ARE on hand.
 */
export const SOURCE_THRESHOLDS = Object.freeze({
  onHand: 1.0,
  make: 0.3,
  buy: 0.1,
});

/**
 * The [0,1] source score for a candidate.
 *   on-hand → thresholds.onHand
 *   make    → thresholds.make
 *   buy     → thresholds.buy + (thresholds.make − thresholds.buy) · availableFraction
 *             (0% of ingredients on hand → buy; approaches make as coverage → 100%)
 * Unknown category → 0. Thresholds are injectable (debug sliders / future
 * per-user settings); defaults to SOURCE_THRESHOLDS.
 *
 * @param category         'on-hand' | 'make' | 'buy'
 * @param availableFraction [0,1] fraction of (non-pantry) ingredients on hand
 *                          (only used for 'buy')
 * @param thresholds       { onHand, make, buy }
 */
export function sourceScore(category, availableFraction = 0, thresholds = SOURCE_THRESHOLDS) {
  const t = thresholds ?? SOURCE_THRESHOLDS;
  if (category === 'on-hand') return t.onHand ?? 0;
  if (category === 'make') return t.make ?? 0;
  if (category === 'buy') {
    const lo = t.buy ?? 0;
    const hi = t.make ?? 0;
    const frac = Math.min(1, Math.max(0, Number(availableFraction) || 0));
    return lo + (hi - lo) * frac;
  }
  return 0;
}

/**
 * EXTRA SERVES — after the generator places ONE serve of a fill-slot entry that
 * can have its count changed (see menu-edit `canChangeServings`), it keeps
 * rolling for another serve until a roll fails. The chance is ENERGY-SCALED so
 * small items repeat more often than big ones:
 *
 *   chance = base × (referenceKj ÷ the entry's CURRENT energy), capped at maxChance
 *
 * Inversely proportional to energy, so the EXPECTED extra energy per roll is
 * roughly constant whatever the item's size; and because it uses the entry's
 * running total, each further serve gets less likely (a 20 kcal snack: 75% →
 * 75% → 75% → 62% …; a 100 kcal snack: 50% → 25% → 17%; 200 kcal: 25% → 12.5%).
 * Hard-coded but isolated here, like FACTOR_WEIGHTS, so they can be tuned later.
 */
export const EXTRA_SERVE = Object.freeze({
  base: 0.25,
  referenceKj: 200 * 4.184, // 200 kcal
  maxChance: 0.75,
});

/**
 * Chance [0, maxChance] of adding ANOTHER serve to an entry currently worth
 * `entryKj` (its servings × kJ per serve). A non-positive/unknown energy → 0
 * (no energy to scale by, and nothing would ever bound the repeats).
 */
export function extraServeChance(entryKj, params = EXTRA_SERVE) {
  const kj = Number(entryKj);
  if (!Number.isFinite(kj) || kj <= 0) return 0;
  return Math.min(params.maxChance, (params.base * params.referenceKj) / kj);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** How many days-to-expiry maps to a score of 0 (anything ≥ this is not urgent). */
export const EXPIRY_HORIZON_DAYS = 7;
/** Extra weight per ADDITIONAL expiring draw in a meal (rewards clearing several). */
export const EXPIRY_COUNT_BONUS = 0.15;

/**
 * Score ONE dated draw by how soon it expires, on a LOGARITHMIC curve: ≤ 1 day
 * left → 1, ≥ EXPIRY_HORIZON_DAYS (7) days → 0, with the drop front-loaded so the
 * gap between 1 and 2 days is larger than between 2 and 3. Days are floored whole
 * days from `now`.
 *
 *   days ≤ 1        → 1
 *   1 < days < 7    → 1 − ln(days)/ln(7)     (≈ .64 @2, .44 @3, .29 @4, …)
 *   days ≥ 7        → 0
 *
 * @param expiry Date the draw expires
 * @param now    reference instant (defaults to current time)
 */
export function drawExpiryScore(expiry, now = new Date()) {
  if (!(expiry instanceof Date)) return 0;
  const nowMs = now instanceof Date ? now.getTime() : Number(now) || Date.now();
  const days = Math.floor((expiry.getTime() - nowMs) / MS_PER_DAY);
  if (days <= 1) return 1;
  if (days >= EXPIRY_HORIZON_DAYS) return 0;
  const s = 1 - Math.log(days) / Math.log(EXPIRY_HORIZON_DAYS);
  return Math.min(1, Math.max(0, s));
}

/**
 * The ABSOLUTE expiry score for ONE candidate from the dated expiries of the
 * stock it would actually draw. A meal with NO perishable (dated) draws scores 0
 * — buying fresh or using indefinite/pantry stock earns nothing on this axis.
 * Otherwise it's the AVERAGE per-draw score, boosted by a mild count bonus so a
 * meal clearing several expiring items outranks one of equal urgency:
 *
 *   score = clamp( avg · (1 + 0.15·(n−1)), 0, 1 )
 *
 * @param expiries array of Date (the dated draws); non-dates are ignored
 * @param now      reference instant (injectable for tests)
 */
export function expiryScore(expiries, now = new Date()) {
  const dated = (expiries ?? []).filter((d) => d instanceof Date);
  const n = dated.length;
  if (n === 0) return 0; // no perishable components → no expiry pressure
  const avg = dated.reduce((s, d) => s + drawExpiryScore(d, now), 0) / n;
  const boosted = avg * (1 + EXPIRY_COUNT_BONUS * (n - 1));
  return Math.min(1, Math.max(0, boosted));
}

/**
 * The rating factor score for a candidate — the effective rating is already a
 * normalised [0,1] value, so it IS the score. (A rating of 0 is handled as a
 * viability gate upstream, not here.)
 */
export function ratingScore(rating) {
  return typeof rating === 'number' && Number.isFinite(rating)
    ? Math.min(1, Math.max(0, rating))
    : 0;
}

/** Weighted sum of per-factor scores using the given weights. */
export function combineWeight(scores, weights = FACTOR_WEIGHTS) {
  let total = 0;
  for (const [factor, weight] of Object.entries(weights)) {
    total += (weight ?? 0) * (scores[factor] ?? 0);
  }
  return total;
}

/**
 * Compute a combined selection weight for every candidate.
 *
 * Each candidate provides `{ source, expiries, rating }`. Expiry is an ABSOLUTE
 * per-candidate score (see expiryScore) from the dated draws that candidate would
 * take — no longer normalised across the set. Returns an array of non-negative
 * weights index-aligned with `candidates`.
 *
 * @param candidates array of { source, availableFraction, expiries: Date[], rating }
 * @param weights    factor weights (defaults to FACTOR_WEIGHTS)
 * @param thresholds source-factor thresholds (defaults to SOURCE_THRESHOLDS)
 * @param now        reference instant for the expiry curve (injectable)
 */
export function candidateWeights(
  candidates,
  weights = FACTOR_WEIGHTS,
  thresholds = SOURCE_THRESHOLDS,
  now = new Date(),
) {
  return candidates.map((c) =>
    combineWeight(
      {
        source: sourceScore(c.source, c.availableFraction ?? 0, thresholds),
        expiry: expiryScore(c.expiries ?? [], now),
        rating: ratingScore(c.rating),
      },
      weights,
    ),
  );
}

/**
 * TWO-STAGE selection probabilities. Picking every candidate straight in
 * proportion to its weight DILUTES a small group: one leftover competing with
 * twenty make/buy recipes wins only ~1 time in 10, however strongly it's
 * preferred, because the many smaller weights add up. So the pick is grouped:
 *
 *   1. choose a GROUP — by default the candidate's source category (on-hand /
 *      make / buy) — in proportion to the group's BEST combined weight;
 *   2. choose a candidate within that group in proportion to its weight.
 *
 * A group's chance therefore reflects how good its best option is, not how many
 * options it has, while every factor (and its weight) still matters at both
 * stages. With all weights 0 the pick falls back to uniform over candidates.
 *
 * @param groups  array of group keys, index-aligned with `weights`
 * @param weights array of non-negative combined weights
 * @returns array of probabilities (sums to 1; [] for no candidates)
 */
export function groupedProbabilities(groups, weights) {
  const n = weights?.length ?? 0;
  if (n === 0) return [];
  const pos = (w) => (w > 0 ? w : 0);
  const byGroup = new Map(); // key → { idx: [], max, sum }
  weights.forEach((w, i) => {
    const key = groups?.[i] ?? '';
    const g = byGroup.get(key) ?? { idx: [], max: 0, sum: 0 };
    g.idx.push(i);
    g.max = Math.max(g.max, pos(w));
    g.sum += pos(w);
    byGroup.set(key, g);
  });
  let groupTotal = 0;
  for (const g of byGroup.values()) groupTotal += g.max;
  if (groupTotal <= 0) return weights.map(() => 1 / n);
  const p = new Array(n).fill(0);
  for (const g of byGroup.values()) {
    if (g.max <= 0) continue; // max > 0 ⇒ sum > 0
    const share = g.max / groupTotal;
    for (const i of g.idx) p[i] = (share * pos(weights[i])) / g.sum;
  }
  return p;
}

/**
 * Combined weights + two-stage selection probabilities for candidates shaped
 * like candidateWeights' input (`{ source, availableFraction, expiries, rating }`),
 * grouped by `source`. The ONE place the generator, the debug explainer and the
 * debug simulator get their pick odds from.
 * @returns {{ weights: number[], probabilities: number[] }}
 */
export function candidateProbabilities(
  candidates,
  weights = FACTOR_WEIGHTS,
  thresholds = SOURCE_THRESHOLDS,
  now = new Date(),
) {
  const w = candidateWeights(candidates, weights, thresholds, now);
  return {
    weights: w,
    probabilities: groupedProbabilities(
      candidates.map((c) => c.source),
      w,
    ),
  };
}

/**
 * The reference instant for scoring expiry when planning `date` (YYYY-MM-DD):
 * the start of that local day, but never earlier than `now` — so a meal planned
 * for next week judges a leftover by how close to expiry it will be THEN, not
 * how far off it looks today. No/invalid date → `now`.
 */
export function planningInstant(date, now = new Date()) {
  const nowMs = now instanceof Date ? now.getTime() : Number(now) || Date.now();
  const day = date ? new Date(`${date}T00:00:00`).getTime() : Number.NaN;
  return new Date(Number.isFinite(day) ? Math.max(day, nowMs) : nowMs);
}

/**
 * Pick one index into `weights` at random, in proportion to weight. Falls back
 * to a uniform pick when every weight is 0 (or the list is degenerate). Returns
 * -1 for an empty list.
 *
 * @param weights array of non-negative numbers
 * @param rng     () => [0,1)
 */
export function weightedPickIndex(weights, rng = Math.random) {
  const n = weights.length;
  if (n === 0) return -1;
  const total = weights.reduce((s, w) => s + (w > 0 ? w : 0), 0);
  if (total <= 0) return Math.floor(rng() * n) % n; // all-zero → uniform
  let r = rng() * total;
  for (let i = 0; i < n; i += 1) {
    const w = weights[i] > 0 ? weights[i] : 0;
    if (r < w) return i;
    r -= w;
  }
  return n - 1; // rounding guard
}

/**
 * Score `candidates` by the weighted-factor model and return the chosen one,
 * using the two-stage grouped pick (see groupedProbabilities).
 * Each candidate is `{ recipe, src, rating }` where `src` is
 * `{ source, draws }` (as produced by the generator's source resolver — the
 * LABELLED dated stock draws `{ expiry, name, kind }` the candidate would take).
 * Returns the chosen candidate object, or null for an empty list.
 */
export function pickWeightedCandidate(
  candidates,
  rng = Math.random,
  weights = FACTOR_WEIGHTS,
  thresholds = SOURCE_THRESHOLDS,
  now = new Date(),
) {
  if (!candidates || candidates.length === 0) return null;
  const { probabilities } = candidateProbabilities(
    candidates.map((c) => ({
      source: c.src?.source,
      availableFraction: c.src?.availableFraction ?? 0,
      expiries: (c.src?.draws ?? []).map((d) => d.expiry),
      rating: c.rating,
    })),
    weights,
    thresholds,
    now,
  );
  const idx = weightedPickIndex(probabilities, rng);
  return idx < 0 ? null : candidates[idx];
}
